import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckIcon, MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";

const initials = (name = "") =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "?";

/**
 * Inline multi-select for people (design §3 «Personvelger (flervalg)»): selected tags,
 * search, a bounded list with check marks. Used in «Tilgang og lesekrav».
 */
export const CompliancePeoplePicker = ({ people = [], value = [], onChange, disabled, idPrefix = "people" }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const selected = useMemo(() => new Set(value.map(Number)), [value]);
  const byId = useMemo(() => new Map(people.map((person) => [Number(person.id), person])), [people]);
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return people;
    return people.filter((person) =>
      `${person.name || ""} ${person.email || ""}`.toLowerCase().includes(needle)
    );
  }, [people, query]);

  const toggle = (id) => {
    if (disabled) return;
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange?.([...next]);
  };

  return (
    <div className="flex flex-col gap-2">
      {selected.size > 0 ? (
        <div className="flex flex-wrap gap-1.5" aria-label={t("hseCompliance.people.selected", { count: selected.size })}>
          {[...selected].map((id) => {
            const person = byId.get(id);
            if (!person) return null;
            return (
              <span key={id} className="pm-chip" aria-pressed="true">
                <span className="pm-picker-avatar" style={{ width: 22, height: 22, fontSize: 9.5 }} aria-hidden="true">
                  {initials(person.name)}
                </span>
                {person.name}
                <button
                  type="button"
                  className="pm-picker-clear"
                  style={{ width: 20, height: 20 }}
                  aria-label={t("hseCompliance.people.remove", { name: person.name })}
                  disabled={disabled}
                  onClick={() => toggle(id)}
                >
                  <XMarkIcon className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </span>
            );
          })}
        </div>
      ) : null}
      <div className="overflow-hidden rounded-[11px] border border-[color:var(--pls-border)] bg-[color:var(--pls-surface)]">
        <div className="pm-picker-head">
          <label className="pm-picker-search" htmlFor={`${idPrefix}-search`}>
            <MagnifyingGlassIcon className="h-4 w-4 text-[color:var(--pls-muted)]" aria-hidden="true" />
            <input
              id={`${idPrefix}-search`}
              value={query}
              disabled={disabled}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t("hseCompliance.people.search")}
              autoComplete="off"
            />
          </label>
        </div>
        <ul className="pm-picker-list" role="listbox" aria-multiselectable="true" style={{ maxHeight: 220 }}>
          {filtered.length === 0 ? (
            <li className="pm-picker-state">{t("hseCompliance.people.empty")}</li>
          ) : (
            filtered.map((person) => {
              const id = Number(person.id);
              const isSelected = selected.has(id);
              return (
                <li
                  key={id}
                  role="option"
                  aria-selected={isSelected}
                  tabIndex={disabled ? -1 : 0}
                  className="pm-picker-option"
                  onClick={() => toggle(id)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      toggle(id);
                    }
                  }}
                >
                  <span className="pm-picker-avatar" aria-hidden="true">{initials(person.name)}</span>
                  <span className="min-w-0 flex-1">
                    <span className="pm-picker-option__primary">{person.name}</span>
                    {person.email ? <span className="pm-picker-secondary">{person.email}</span> : null}
                  </span>
                  <CheckIcon className="pm-picker-option__check" aria-hidden="true" />
                </li>
              );
            })
          )}
        </ul>
      </div>
    </div>
  );
};

export default CompliancePeoplePicker;
